import { FC, ReactElement, useEffect } from 'react'
import classNames from 'classnames'
import formatNumber from '@/utils/format-number'
import { Input } from '@/components/ui/input'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { NETWORK } from '@/utils/constants'
import useRoundedSatoshiPrice from '@/utils/hooks/useRoundedSatoshiPrice'
import isFungible from '@/utils/is-fungible'

import BaseItem from './base-item'

const ListItem: FC<{
	onSelect: () => void
	selected: boolean
	price: number | string
	defaultPrice?: number | string
	onPriceChange: (evt: any) => void
	slug: string
	step: number
	data: any
}> = (props): ReactElement => {
	const { data, onSelect, selected, price, defaultPrice, onPriceChange, slug, step } = props
	const amount = parseFloat(data.amount)
	const { exchangeRate } = useExchangeRate()
	const currency = isFungible(slug) ? 'sats' : NETWORK

	// Derive escrow per-unit price if listed
	const escrowPrice = data.escrow?.satoshi_price
		? (() => {
				const perUnit =
					data.escrow.satoshi_price / parseFloat(data.escrow.amount || data.amount || '1')
				return currency === NETWORK ? perUnit / 1e8 : perUnit
		  })()
		: null

	const selectedPrice = price === '' || price === null || price === undefined ? null : Number(price)
	const displayPrice = selected ? selectedPrice : escrowPrice
	const roundedSats = useRoundedSatoshiPrice(
		(displayPrice || 0) * amount * (isFungible(slug) ? 1 : 1e8),
		1e6
	)
	const usd = formatNumber(
		((((displayPrice || 0) * amount) / (isFungible(slug) ? 1e8 : 1)) * exchangeRate).toFixed(2)
	)

	const stopPropogation = (evt: any) => {
		if (selected) {
			evt.stopPropagation()
		}
	}

	useEffect(() => {
		const nextPrice = defaultPrice ?? escrowPrice ?? ''
		if (selected && (price === '' || price === null || price === undefined) && nextPrice !== '') {
			onPriceChange({ target: { value: nextPrice } })
		}
	}, [defaultPrice, escrowPrice, onPriceChange, price, selected])

	// A lot that is already on the market, or sold and waiting for its
	// (privately relayed) sale to mine, cannot be listed again.
	const shared = Number(data.shared_utxo_count) > 1
	const locked = !!data.escrow || data.pending_sale === true || shared
	return (
		<BaseItem data={data} onSelect={onSelect} selected={selected} slug={slug} disabled={locked}>
			{shared ? (
				<span
					className="text-[10px] font-medium px-2 py-1 rounded bg-muted text-muted-foreground select-none cursor-help"
					title={`This UTXO holds ${data.shared_utxo_count} inscriptions, so it cannot be listed on its own. Send this inscription to yourself to put it on its own UTXO, then list it.`}
				>
					Shared UTXO · split first
				</span>
			) : data.pending_sale === true ? (
				<span className="text-[10px] font-medium px-2 py-1 rounded bg-amber-500/20 text-amber-600 dark:text-amber-300 select-none">
					Sale pending
				</span>
			) : (
				!!data.escrow && (
					<span className="text-[10px] font-medium px-2 py-1 rounded bg-violet-500 text-white select-none">
						Listed
					</span>
				)
			)}
			{(selected || escrowPrice) && !!displayPrice && (
				<p className="text-xxs text-muted-foreground">
					{roundedSats} {NETWORK}/${usd}
				</p>
			)}
			<Input
				value={selected ? price ?? '' : escrowPrice || ''}
				placeholder="--"
				onClick={stopPropogation}
				onKeyDown={stopPropogation}
				onChange={onPriceChange}
				type="number"
				step={currency === NETWORK ? step : 1}
				className={classNames(
					'text-xs py-0 px-2 h-7 w-24 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
					{
						['text-muted-foreground']: !price
					}
				)}
			/>
		</BaseItem>
	)
}

export default ListItem
